import { readAuthSessionClaims } from '../auth-session-claims';
import { isSameAuthSession } from './native-auth-bootstrap';

import type { Session } from '@supabase/supabase-js';
import type { NativeAuthSessionRead } from './auth-persistence';
import type {
  NativeAuthCallbackOutcome,
  NativeAuthCallbackPreviousIdentity,
} from './native-auth-launch';

export type ApplyAuthStateChange = (
  event: string,
  session: Session | null,
  previousUserIdOverride?: string | null,
  forceIdentityReplacement?: boolean,
) => Promise<void>;

export interface StoredSessionQuarantine {
  blockedIdentity: NativeAuthCallbackPreviousIdentity | null;
  blockedSession: Session | null;
  forceReplacementBoundaryPending: boolean;
  learnNextSession: boolean;
  replacementBoundaryPending: boolean;
}

export function createStoredSessionQuarantine(): StoredSessionQuarantine {
  return {
    blockedIdentity: null,
    blockedSession: null,
    forceReplacementBoundaryPending: false,
    learnNextSession: false,
    replacementBoundaryPending: false,
  };
}

function matchesBlockedIdentity(
  session: Session,
  identity: NativeAuthCallbackPreviousIdentity,
): boolean {
  if (identity.status !== 'available' || !identity.userId || session.user.id !== identity.userId) {
    return false;
  }
  if (!identity.sessionId) {
    return true;
  }
  return readAuthSessionClaims(session.access_token)?.sessionId === identity.sessionId;
}

function suppressInitialSession(event: string, onSuppressed: () => void): void {
  if (event === 'INITIAL_SESSION') {
    onSuppressed();
  }
}

async function applySessionEvent(
  event: string,
  session: Session,
  quarantine: StoredSessionQuarantine,
  apply: ApplyAuthStateChange,
  onSuppressed: () => void,
): Promise<void> {
  const {
    blockedIdentity,
    blockedSession,
    forceReplacementBoundaryPending,
    learnNextSession,
    replacementBoundaryPending,
  } = quarantine;
  if (blockedSession || blockedIdentity) {
    if (
      (blockedSession && isSameAuthSession(session, blockedSession)) ||
      (blockedIdentity && matchesBlockedIdentity(session, blockedIdentity))
    ) {
      suppressInitialSession(event, onSuppressed);
      return;
    }
    if (replacementBoundaryPending) {
      quarantine.replacementBoundaryPending = false;
      await apply(
        event,
        session,
        blockedSession?.user.id ??
          (blockedIdentity?.status === 'available' ? blockedIdentity.userId : null),
      );
      return;
    }
    await apply(event, session);
    return;
  }
  if (learnNextSession) {
    quarantine.blockedIdentity = null;
    quarantine.blockedSession = session;
    quarantine.forceReplacementBoundaryPending = false;
    quarantine.learnNextSession = false;
    quarantine.replacementBoundaryPending = true;
    suppressInitialSession(event, onSuppressed);
    return;
  }
  if (forceReplacementBoundaryPending) {
    quarantine.forceReplacementBoundaryPending = false;
    await apply(event, session, null, true);
    return;
  }
  await apply(event, session);
}

export async function applyQuarantinedAuthEvent(
  event: string,
  session: Session | null,
  quarantine: StoredSessionQuarantine,
  apply: ApplyAuthStateChange,
  onSuppressed: () => void,
): Promise<void> {
  if (session) {
    await applySessionEvent(event, session, quarantine, apply, onSuppressed);
    return;
  }
  quarantine.learnNextSession = false;
  if (event === 'SIGNED_OUT') {
    quarantine.blockedIdentity = null;
    quarantine.blockedSession = null;
    quarantine.forceReplacementBoundaryPending = false;
    quarantine.replacementBoundaryPending = false;
  }
  await apply(event, null);
}

export async function applySuccessfulCallback(
  outcome: Extract<NativeAuthCallbackOutcome, { status: 'success' }>,
  quarantine: StoredSessionQuarantine,
  apply: ApplyAuthStateChange,
): Promise<void> {
  const previousUserId =
    outcome.previousIdentity.status === 'available' ? outcome.previousIdentity.userId : null;
  if (
    outcome.previousIdentity.status === 'available' &&
    outcome.previousIdentity.userId &&
    !matchesBlockedIdentity(outcome.session, outcome.previousIdentity)
  ) {
    quarantine.blockedIdentity = outcome.previousIdentity;
  }
  await (outcome.previousIdentity.status === 'unavailable'
    ? apply('SIGNED_IN', outcome.session, null, true)
    : apply('SIGNED_IN', outcome.session, previousUserId));
}

export function quarantineFailedCallback(
  storedRead: NativeAuthSessionRead,
  quarantine: StoredSessionQuarantine,
  onSuppressed: () => void,
): void {
  const storedSession = storedRead.status === 'available' ? storedRead.session : null;
  quarantine.blockedIdentity = null;
  quarantine.blockedSession = storedSession;
  quarantine.learnNextSession = storedRead.status === 'unavailable';
  quarantine.forceReplacementBoundaryPending = storedRead.status === 'unavailable';
  quarantine.replacementBoundaryPending = storedSession !== null;
  onSuppressed();
}
